/**
 * Contenido editorial por herramienta.
 *
 * Es lo que convierte una calculadora en una página indexable. Sin
 * esto, cada ruta sería un formulario suelto: exactamente el perfil de
 * "contenido de poco valor" que AdSense penaliza y que Google no
 * posiciona.
 *
 * Las `faqs` se publican además como datos estructurados FAQPage.
 */

export const CONTENIDO = {
  tool_iva: {
    fuente: "Ley del Impuesto al Valor Agregado (LIVA)",
    secciones: [
      {
        titulo: "Cómo se calcula el IVA en México",
        parrafos: [
          "El Impuesto al Valor Agregado es un impuesto indirecto que grava el consumo. En México la tasa general es del <strong>16%</strong>, y existe una tasa reducida del <strong>8%</strong> para la región fronteriza norte y sur, además de una tasa del 0% para ciertos productos de la canasta básica, medicinas y libros.",
          "Sumar el IVA es sencillo: multiplicas el subtotal por la tasa y lo agregas. Con 16%, mil pesos de subtotal se convierten en 1,160 pesos de total.",
          "Desglosarlo confunde a mucha gente, porque la operación no es restar el 16%. Si tienes un precio de 1,160 pesos con IVA incluido y le restas el 16%, obtienes 974.40, que es incorrecto. Lo correcto es <strong>dividir entre 1.16</strong>, lo que devuelve los 1,000 pesos originales. La diferencia existe porque el porcentaje se calcula sobre el subtotal, no sobre el total.",
        ],
        lista: [
          "<strong>Agregar IVA:</strong> subtotal × 1.16 = total",
          "<strong>Desglosar IVA:</strong> total ÷ 1.16 = subtotal",
          "<strong>Obtener sólo el impuesto:</strong> total − subtotal",
        ],
      },
      {
        titulo: "Cuándo aplica la tasa del 8%",
        parrafos: [
          "El estímulo fiscal de la región fronteriza reduce el IVA al 8% en municipios de la frontera norte y sur. No aplica automáticamente por estar en esa zona: el contribuyente debe estar inscrito en el padrón correspondiente ante el SAT y cumplir requisitos, entre ellos tener el domicilio fiscal en la región y realizar la entrega material de los bienes en ella.",
          "Si emites o recibes facturas en esa zona, conviene confirmar la tasa aplicable antes de calcular, porque un error de tasa se arrastra a toda la contabilidad del periodo.",
        ],
      },
      {
        titulo: "Errores frecuentes al calcular el IVA",
        parrafos: [
          "El más común, con diferencia, es restar el porcentaje en lugar de dividir. El segundo es acumular el redondeo: cuando desglosas partida por partida y luego sumas, el total puede diferir en algunos centavos del cálculo hecho sobre el total. Para efectos fiscales, lo que manda es el criterio de redondeo de tu sistema de facturación.",
          "El tercero es confundir el IVA con la retención de IVA. En ciertos servicios entre personas morales y físicas se retienen dos terceras partes del impuesto; ese es un cálculo distinto que depende del tipo de operación.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cómo le quito el IVA a un precio?",
        respuesta:
          "Divide el precio final entre 1.16 si la tasa es del 16%, o entre 1.08 si es del 8%. El resultado es el subtotal. La diferencia entre el precio final y ese subtotal es el IVA. No restes el 16% directamente: da un resultado incorrecto.",
      },
      {
        pregunta: "¿Cuál es la tasa de IVA en México en 2026?",
        respuesta:
          "La tasa general es del 16%. En la región fronteriza norte y sur aplica una tasa reducida del 8% para contribuyentes inscritos en el padrón correspondiente. Algunos bienes y servicios, como alimentos básicos, medicinas y libros, están gravados al 0%.",
      },
      {
        pregunta: "¿Por qué restar el 16% no funciona para desglosar el IVA?",
        respuesta:
          "Porque el 16% se calculó sobre el subtotal, no sobre el total. Al restar el 16% del total estás quitando un porcentaje de una base mayor a la original, así que te pasas. La operación inversa correcta de multiplicar por 1.16 es dividir entre 1.16.",
      },
      {
        pregunta: "¿El IVA es igual en toda América Latina?",
        respuesta:
          "No. Cada país tiene su propia tasa: Argentina y España aplican 21%, Colombia y Chile 19%, Perú 18%, Guatemala y Ecuador 12%. Esta calculadora incluye las principales para que puedas comparar operaciones internacionales.",
      },
    ],
  },

  tool_sueldo: {
    fuente: "Ley del ISR, Ley del Seguro Social y Ley Federal del Trabajo",
    secciones: [
      {
        titulo: "Por qué tu sueldo neto es menor que el bruto",
        parrafos: [
          "Entre lo que dice tu contrato y lo que llega a tu cuenta hay dos descuentos obligatorios: el <strong>ISR</strong>, que retiene tu patrón y entera al SAT, y las <strong>cuotas obrero del IMSS</strong>, tu parte de la seguridad social. El patrón paga además su propia porción de las cuotas, que no te descuentan pero sí forman parte del costo de tenerte contratado.",
          "El ISR en México es progresivo: no se aplica un porcentaje único a todo tu sueldo, sino que se ubica tu ingreso en un tramo de la tarifa del SAT, se toma una cuota fija y se aplica el porcentaje del tramo únicamente sobre lo que excede el límite inferior. Por eso subir de tramo nunca hace que ganes menos en términos netos.",
          "A esa retención se le resta el <strong>subsidio para el empleo</strong> cuando el ingreso está por debajo del tope establecido. Para sueldos bajos el subsidio puede reducir la retención a cero.",
        ],
      },
      {
        titulo: "Qué es el Salario Base de Cotización (SBC)",
        parrafos: [
          "El SBC es el salario sobre el que se calculan tus cuotas del IMSS y, más adelante, tu pensión. No es tu salario diario a secas: incluye la parte proporcional de las prestaciones que recibes durante el año.",
          "Se obtiene multiplicando tu salario diario por el <strong>factor de integración</strong>, que suma el aguinaldo y la prima vacacional prorrateados entre los 365 días del año:",
        ],
        lista: [
          "Factor = 1 + (días de aguinaldo ÷ 365) + (días de vacaciones × prima vacacional ÷ 365)",
          "Con los mínimos de ley (15 días de aguinaldo, 12 de vacaciones y 25% de prima) el factor es 1.0493",
          "Mejores prestaciones suben el factor, el SBC y también tus cuotas — pero mejoran tu pensión",
        ],
      },
      {
        titulo: "Días de vacaciones desde la reforma de 2023",
        parrafos: [
          "La reforma conocida como «Vacaciones Dignas», vigente desde enero de 2023, duplicó el mínimo del primer año: de 6 a <strong>12 días</strong>. A partir de ahí suben dos días por año hasta el quinto, y luego dos días por cada bloque de cinco años.",
          "Esto importa para tu sueldo neto porque los días de vacaciones entran en el factor de integración: más vacaciones significan un SBC ligeramente mayor.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cuánto me descuentan de ISR si gano 18,000 pesos al mes?",
        respuesta:
          "Con un sueldo bruto de 18,000 pesos mensuales caes en el tramo del 17.92%, pero ese porcentaje sólo se aplica sobre el excedente del límite inferior del tramo, más una cuota fija. La retención resultante ronda los 1,860 pesos, a los que hay que sumar unos 600 pesos de cuotas del IMSS. Usa la calculadora para el número exacto con tus prestaciones.",
      },
      {
        pregunta: "¿Qué es el factor de integración y para qué sirve?",
        respuesta:
          "Es el multiplicador que convierte tu salario diario en Salario Base de Cotización. Suma al salario la parte proporcional del aguinaldo y de la prima vacacional. Con los mínimos de ley vale 1.0493. El SBC determina tus cuotas del IMSS y el monto de tu futura pensión.",
      },
      {
        pregunta: "¿El subsidio para el empleo sigue existiendo?",
        respuesta:
          "Sí. Se calcula como un porcentaje de la UMA mensual y aplica a trabajadores cuyo ingreso no rebasa el tope establecido. Reduce directamente el ISR a cargo y, en sueldos bajos, puede dejarlo en cero. Esta calculadora lo aplica automáticamente cuando corresponde.",
      },
      {
        pregunta: "¿Subir de tramo de ISR me hace ganar menos?",
        respuesta:
          "No. Es un mito extendido. La tarifa es progresiva: el porcentaje más alto sólo se aplica a la parte del ingreso que excede el límite inferior del tramo, no a todo tu sueldo. Ganar un peso más siempre deja más dinero neto.",
      },
      {
        pregunta: "¿Cuántos días de vacaciones me tocan?",
        respuesta:
          "Desde la reforma de 2023: 12 días el primer año, 14 el segundo, 16 el tercero, 18 el cuarto y 20 el quinto. A partir del sexto año suben dos días por cada cinco años de servicio.",
      },
    ],
  },

  tool_infonavit: {
    fuente: "Reglas de otorgamiento de crédito del INFONAVIT",
    secciones: [
      {
        titulo: "Cómo funciona un crédito INFONAVIT",
        parrafos: [
          "El INFONAVIT otorga créditos de vivienda con una tasa que depende de tu <strong>nivel de ingreso</strong>: cuanto menor es el salario, menor es la tasa. Va desde alrededor del 3.09% anual para los ingresos más bajos hasta el 10.45% para los más altos.",
          "El pago mensual se descuenta directamente de tu nómina y está topado: el instituto no puede descontarte más de un porcentaje determinado de tu salario, entre el 20% y el 30% según tu nivel de ingreso. Si el pago que requeriría tu crédito supera ese tope, el monto autorizado se ajusta o el plazo se alarga.",
          "Además, tu patrón aporta cada mes el equivalente al <strong>5% de tu salario</strong> a tu subcuenta de vivienda. Mientras tienes un crédito activo, esa aportación se abona directamente a capital, lo que acorta el plazo real sin que tú pongas un peso adicional.",
        ],
      },
      {
        titulo: "Por qué los pagos extra ahorran tanto",
        parrafos: [
          "En un crédito a 20 o 30 años, los primeros años casi todo tu pago se va a intereses y muy poco a capital. Un abono voluntario, en cambio, se aplica <strong>íntegro a capital</strong>: reduce el saldo sobre el que se calculan todos los intereses futuros.",
          "El efecto es desproporcionado. Un abono anual modesto sostenido en el tiempo puede recortar años del crédito y ahorrar cientos de miles de pesos en intereses, porque cada peso que quitas del saldo hoy te ahorra los intereses de ese peso durante todos los años que restaban.",
          "El simulador compara tres escenarios lado a lado: sólo tu pago mensual, tu pago más la aportación patronal, y ambos más la estrategia de abonos que definas.",
        ],
        lista: [
          "Los abonos tempranos ahorran más que los tardíos: el interés se acumula sobre el saldo",
          "Confirma siempre que el abono se aplique a capital y no como pago adelantado de mensualidades",
          "La aportación patronal del 5% ya trabaja a tu favor aunque no hagas nada",
        ],
      },
      {
        titulo: "Qué no incluye esta simulación",
        parrafos: [
          "El simulador calcula la amortización financiera del crédito. No incluye los gastos de originación, el avalúo, los gastos notariales ni el seguro de daños y de vida, que se cobran aparte y varían según el caso.",
          "Tampoco contempla los esquemas en Veces Salario Mínimo ni las reestructuras. Úsalo para entender el impacto de tus decisiones de pago, y confirma las cifras exactas en tu estado de cuenta o en Mi Cuenta INFONAVIT.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cuánto me pueden descontar de mi sueldo por el crédito INFONAVIT?",
        respuesta:
          "El descuento está topado según tu nivel de ingreso: va del 20% para los salarios más bajos hasta el 30% para los más altos. Si la mensualidad que requeriría tu crédito rebasa ese tope, el INFONAVIT ajusta el monto o el plazo.",
      },
      {
        pregunta: "¿Conviene hacer pagos extra a mi crédito INFONAVIT?",
        respuesta:
          "Sí, especialmente en los primeros años. Un abono voluntario se aplica directamente a capital, reduciendo el saldo sobre el que se calculan todos los intereses futuros. Un abono anual sostenido puede recortar varios años del crédito y ahorrar una cantidad importante en intereses.",
      },
      {
        pregunta: "¿Qué es la aportación patronal del 5%?",
        respuesta:
          "Es el porcentaje de tu salario que tu patrón deposita cada mes en tu subcuenta de vivienda. Mientras tienes un crédito activo, ese dinero se abona a capital, acortando el plazo real del crédito sin costo adicional para ti.",
      },
      {
        pregunta: "¿Qué tasa de interés me corresponde?",
        respuesta:
          "Depende de tu ingreso mensual. Los rangos van desde 3.09% anual para los ingresos más bajos hasta 10.45% para los más altos. La calculadora sugiere la tasa correspondiente a tu sueldo, y puedes ajustarla si tu crédito tiene condiciones distintas.",
      },
    ],
  },

  tool_interes: {
    secciones: [
      {
        titulo: "Interés simple e interés compuesto: la diferencia que decide tu patrimonio",
        parrafos: [
          "El <strong>interés simple</strong> se calcula siempre sobre el capital original. Si inviertes 10,000 pesos al 10% anual, ganas 1,000 pesos cada año, siempre los mismos.",
          "El <strong>interés compuesto</strong> se calcula sobre el capital más los intereses ya generados. El segundo año ganas intereses sobre 11,000, el tercero sobre 12,100, y así sucesivamente. La diferencia es imperceptible el primer año y enorme a los veinte.",
          "La frecuencia de capitalización importa: el mismo 10% anual rinde más si se capitaliza mensualmente que si se capitaliza una vez al año, porque los intereses empiezan a generar intereses antes.",
        ],
      },
      {
        titulo: "La regla del 72",
        parrafos: [
          "Para estimar mentalmente cuánto tarda una inversión en duplicarse, divide 72 entre la tasa anual. Al 8%, unos nueve años. Al 12%, unos seis. Es una aproximación, pero sirve para comparar opciones sin calculadora.",
          "Funciona también al revés, y ahí duele: una deuda al 36% anual duplica su saldo en dos años si no la pagas.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cuál es la fórmula del interés compuesto?",
        respuesta:
          "Monto final = Capital × (1 + tasa/n)^(n×años), donde n es el número de capitalizaciones por año. Con capitalización mensual, n = 12.",
      },
      {
        pregunta: "¿Qué significa la capitalización mensual?",
        respuesta:
          "Que los intereses se suman al capital cada mes, y a partir de entonces ese monto mayor genera intereses. A mayor frecuencia de capitalización, mayor rendimiento final con la misma tasa nominal.",
      },
      {
        pregunta: "¿En cuánto tiempo se duplica mi dinero?",
        respuesta:
          "Aproximadamente, divide 72 entre la tasa de interés anual. Al 6% tardarías unos 12 años; al 9%, unos 8. Es la llamada regla del 72.",
      },
    ],
  },

  tool_presupuesto: {
    secciones: [
      {
        titulo: "Qué es la regla 50/30/20",
        parrafos: [
          "Es un método de presupuesto que reparte tu ingreso neto en tres bloques: <strong>50% a necesidades</strong>, <strong>30% a deseos</strong> y <strong>20% a ahorro</strong> o pago de deudas. Su virtud no es la precisión, sino que cabe en la cabeza: no hace falta llevar registro de cada café.",
          "Las necesidades son lo que no puedes dejar de pagar sin consecuencias graves: vivienda, comida, servicios, transporte al trabajo, salud. Los deseos son todo lo que mejora tu vida pero podrías recortar: restaurantes, suscripciones, ocio, compras.",
          "El ahorro incluye el fondo de emergencia, las inversiones y los pagos a deuda por encima del mínimo. Esa distinción importa: pagar el mínimo de la tarjeta es una necesidad; pagar de más es ahorro, porque reduce intereses futuros.",
        ],
      },
      {
        titulo: "Cuando los números no cuadran",
        parrafos: [
          "En México, y sobre todo en las grandes ciudades, la vivienda sola puede llevarse más del 50%. Si tus necesidades se pasan, el reparto no está roto: te está diciendo dónde está la presión.",
          "Ante eso hay tres palancas y ninguna es cómoda: bajar el costo fijo más grande (normalmente la vivienda o el transporte), aumentar el ingreso, o aceptar temporalmente un ahorro menor al 20% con un plan explícito para volver. Lo que no funciona es no mirarlo.",
        ],
        lista: [
          "Empieza por el fondo de emergencia: de tres a seis meses de gastos fijos",
          "Una deuda con interés alto rinde más que casi cualquier inversión: liquidarla es ahorrar",
          "Automatiza el ahorro el día de la quincena, no a fin de mes con lo que sobre",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Qué gastos cuentan como necesidades?",
        respuesta:
          "Los que no puedes dejar de pagar sin consecuencias graves: renta o hipoteca, alimentación básica, servicios del hogar, transporte al trabajo, salud y seguros, y los pagos mínimos de deudas.",
      },
      {
        pregunta: "¿Qué hago si mis gastos fijos superan el 50%?",
        respuesta:
          "Es muy común en ciudades caras. Las opciones son reducir el gasto fijo más grande (vivienda o transporte), aumentar el ingreso, o ajustar temporalmente el porcentaje de ahorro con un plan concreto para recuperarlo. Lo importante es que sea una decisión consciente y no un descuido.",
      },
      {
        pregunta: "¿El pago de mis deudas cuenta como ahorro?",
        respuesta:
          "El pago mínimo es una necesidad. Lo que pagas por encima del mínimo sí cuenta como ahorro, porque reduce el saldo y los intereses que pagarías después.",
      },
    ],
  },

  tool_freelance: {
    secciones: [
      {
        titulo: "Por qué tu tarifa no es tu sueldo dividido entre 160",
        parrafos: [
          "El error clásico al independizarse es tomar el sueldo mensual que se ganaba en nómina, dividirlo entre las horas del mes y cobrar eso. El resultado es una tarifa que no alcanza, y la razón es que como empleado muchas cosas estaban pagadas por otro.",
          "Como freelance pagas tus propios impuestos, tus vacaciones, tus días de enfermedad, tu equipo, tu software, tu contador y los periodos sin clientes. Y sólo puedes facturar una fracción de tus horas: buscar clientes, cotizar, ir a juntas y llevar la administración son horas trabajadas que nadie te paga.",
          "Un <strong>70% de aprovechamiento</strong> es una cifra realista para alguien establecido. Al empezar suele ser bastante menor.",
        ],
      },
      {
        titulo: "Cobrar por proyecto en vez de por hora",
        parrafos: [
          "La tarifa por hora es la base para calcular, pero no siempre la mejor forma de cobrar. Cuando cobras por hora, mejorar tu velocidad reduce tus ingresos: te castiga por ser bueno.",
          "Usa la tarifa por hora para estimar internamente cuánto vale un proyecto, y cotiza un precio cerrado. Si el proyecto te toma menos tiempo del previsto, el beneficio de tu experiencia es tuyo.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cuánto debo cobrar por hora como freelance?",
        respuesta:
          "Parte del ingreso neto que necesitas al mes, súmale tus gastos operativos y los impuestos, y divide entre las horas que realmente puedes facturar al año, no entre todas las horas que trabajas. Esta calculadora hace ese cálculo completo.",
      },
      {
        pregunta: "¿Qué porcentaje de mis horas es facturable?",
        respuesta:
          "Entre 60% y 75% para un freelance establecido. El resto se va en buscar clientes, cotizar, administrar, facturar y capacitarse. Si estás empezando, calcula más cerca del 50%.",
      },
      {
        pregunta: "¿Conviene cobrar por hora o por proyecto?",
        respuesta:
          "Usa la tarifa por hora para calcular internamente, pero cotiza por proyecto cuando puedas. Cobrar por hora penaliza tu eficiencia: entre mejor y más rápido trabajas, menos ganas.",
      },
    ],
  },

  tool_negocio: {
    secciones: [
      {
        titulo: "Margen y markup no son lo mismo",
        parrafos: [
          "Es el error de precios más caro y más común. El <strong>markup</strong> se calcula sobre el costo; el <strong>margen</strong> sobre el precio de venta. Un producto que cuesta 100 y se vende en 150 tiene un markup del 50%, pero un margen del 33.3%.",
          "Quien cree estar ganando 50% cuando gana 33% subestima sistemáticamente cuánto necesita vender, y descubre el problema cuando los gastos fijos no salen.",
        ],
        lista: [
          "Markup = (precio − costo) ÷ costo",
          "Margen = (precio − costo) ÷ precio",
          "El margen nunca puede pasar del 100%; el markup no tiene techo",
        ],
      },
      {
        titulo: "El punto de equilibrio",
        parrafos: [
          "Es el volumen de ventas en el que no ganas ni pierdes: los ingresos cubren exactamente los costos fijos más los variables. Por debajo pierdes dinero; por encima, cada unidad aporta su margen de contribución completo a la utilidad.",
          "Se calcula dividiendo los costos fijos entre el margen de contribución unitario (precio de venta menos costo variable por unidad). Si el margen de contribución es cero o negativo, no existe punto de equilibrio: vender más aumenta la pérdida.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cuál es la diferencia entre margen y markup?",
        respuesta:
          "El markup se calcula sobre el costo y el margen sobre el precio de venta. Un producto de costo 100 vendido en 150 tiene 50% de markup pero 33.3% de margen. Confundirlos hace que subestimes cuánto necesitas vender.",
      },
      {
        pregunta: "¿Cómo calculo el punto de equilibrio?",
        respuesta:
          "Divide tus costos fijos entre el margen de contribución por unidad, que es el precio de venta menos el costo variable unitario. El resultado son las unidades que necesitas vender para no perder ni ganar.",
      },
      {
        pregunta: "¿Qué es un ROI bueno?",
        respuesta:
          "Depende del riesgo y del plazo. Un ROI del 20% en cinco años es mediocre; el mismo 20% en seis meses es excelente. Por eso conviene comparar siempre el ROI anualizado, no el del periodo.",
      },
    ],
  },

  tool_descuentos: {
    secciones: [
      {
        titulo: "Los descuentos no se suman",
        parrafos: [
          "Un 20% de rebaja más un 10% adicional de tarjeta no es un 30%. El segundo descuento se aplica sobre el precio ya rebajado, así que el descuento real es del <strong>28%</strong>.",
          "La diferencia crece con los porcentajes: 50% más 50% no es gratis, es un 75% de descuento. Las promociones encadenadas siempre suenan mejor de lo que son, y el cálculo exacto evita sorpresas en la caja.",
        ],
      },
      {
        titulo: "El precio unitario delata la oferta falsa",
        parrafos: [
          "El tamaño familiar no siempre sale más barato. La única forma de comparar dos presentaciones distintas es reducirlas a la misma unidad: pesos por kilo, por litro o por pieza.",
          "Es especialmente útil frente a promociones tipo «3×2» o «segunda a mitad de precio», donde el precio por unidad efectivo rara vez es el que parece.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cómo se calculan dos descuentos seguidos?",
        respuesta:
          "Se aplican uno después del otro, no se suman. Con 20% y luego 10%: multiplica el precio por 0.80 y el resultado por 0.90. El descuento efectivo es del 28%, no del 30%.",
      },
      {
        pregunta: "¿Cómo sé cuál presentación conviene más?",
        respuesta:
          "Divide el precio entre la cantidad para obtener el precio por unidad (por kilo, litro o pieza) y compara. El envase más grande no siempre tiene el mejor precio unitario.",
      },
    ],
  },

  tool_divisa: {
    secciones: [
      {
        titulo: "Por qué el tipo de cambio que ves no es el que pagas",
        parrafos: [
          "Las tasas que muestra esta herramienta son de referencia del mercado interbancario. Ese es el precio al que se intercambian grandes volúmenes entre instituciones, y casi nunca es el precio al que tú compras.",
          "Tu banco, la casa de cambio o el procesador de tu tarjeta aplican un <strong>diferencial</strong> sobre esa tasa, además de posibles comisiones. Ese diferencial puede ir de menos del 1% en una transferencia optimizada hasta un 5% o más en el aeropuerto.",
          "Usa la tasa de referencia para saber cuánto <em>debería</em> costar la operación, y compárala con lo que te ofrecen: la diferencia es lo que estás pagando por el servicio.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿De dónde salen estos tipos de cambio?",
        respuesta:
          "Del servicio público open.er-api.com, que publica tasas de referencia del mercado. Se actualizan periódicamente. Si el servicio no responde, la herramienta lo indica y muestra tasas aproximadas de respaldo.",
      },
      {
        pregunta: "¿Por qué mi banco me da un tipo de cambio distinto?",
        respuesta:
          "Porque aplica un diferencial sobre la tasa de mercado, más comisiones. Es su margen por el servicio. Comparar la tasa de referencia con la que te ofrecen te dice exactamente cuánto estás pagando de más.",
      },
    ],
  },

  tool_imc: {
    fuente: "Clasificación de la Organización Mundial de la Salud",
    secciones: [
      {
        titulo: "Qué mide realmente el IMC",
        parrafos: [
          "El Índice de Masa Corporal relaciona peso y estatura mediante una fórmula simple: peso en kilogramos dividido entre el cuadrado de la altura en metros. La OMS clasifica el resultado en bajo peso (menos de 18.5), normal (18.5 a 24.9), sobrepeso (25 a 29.9) y obesidad (30 o más).",
          "Fue diseñado como herramienta <strong>poblacional</strong>, para estudiar grupos, no para diagnosticar individuos. Esa distinción es la clave para interpretarlo bien.",
        ],
      },
      {
        titulo: "Sus límites",
        parrafos: [
          "El IMC no distingue entre músculo y grasa. Una persona muy musculosa puede salir en «obesidad» sin tener exceso de grasa, y alguien con poca masa muscular puede salir «normal» teniendo un porcentaje de grasa elevado.",
          "Tampoco considera la distribución de la grasa, que es lo que más se asocia al riesgo cardiovascular, ni ajusta por edad, sexo o complexión. Medidas como el perímetro de cintura complementan mejor el panorama.",
          "Trátalo como un primer indicador, no como un veredicto: un resultado fuera de rango es una razón para consultar a un profesional de salud, no un diagnóstico.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cómo se calcula el IMC?",
        respuesta:
          "Se divide el peso en kilogramos entre el cuadrado de la estatura en metros. Una persona de 70 kg y 1.75 m tiene un IMC de 70 ÷ (1.75 × 1.75) = 22.9.",
      },
      {
        pregunta: "¿Cuál es un IMC normal?",
        respuesta:
          "Según la OMS, entre 18.5 y 24.9. Por debajo se considera bajo peso; entre 25 y 29.9, sobrepeso; y 30 o más, obesidad.",
      },
      {
        pregunta: "¿Es confiable el IMC?",
        respuesta:
          "Es útil como indicador general, pero tiene límites importantes: no distingue músculo de grasa ni considera la distribución corporal, la edad o el sexo. No sustituye una valoración médica.",
      },
    ],
  },

  tool_viaje: {
    secciones: [
      {
        titulo: "Cómo calcular el costo real de un viaje por carretera",
        parrafos: [
          "El gasto de un viaje tiene dos componentes principales: el <strong>combustible</strong>, que depende de la distancia y del rendimiento de tu vehículo, y las <strong>casetas</strong>, que en México pueden representar una parte sorprendentemente grande del total en rutas largas.",
          "Para el combustible: divide los kilómetros entre el rendimiento en km/L para obtener los litros, y multiplícalos por el precio por litro. Un compacto rinde alrededor de 14 km/L en carretera, una SUV cerca de 10 y una pickup unos 8.",
          "El rendimiento real baja con velocidad alta, aire acondicionado, carga y terreno montañoso. Si vas cargado o a más de 110 km/h, calcula con un rendimiento algo menor del nominal.",
        ],
      },
      {
        titulo: "Repartir el gasto entre pasajeros",
        parrafos: [
          "Dividir el total entre quienes viajan es la forma justa y transparente de organizar un viaje compartido, y evita la conversación incómoda al regresar. La calculadora hace el reparto automáticamente.",
          "Si vas a aventar el cálculo antes de salir, considera también el desgaste del vehículo: llantas, aceite y mantenimiento tienen un costo por kilómetro que no ves ese día pero que existe.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cómo calculo cuánta gasolina gasta mi viaje?",
        respuesta:
          "Divide la distancia total en kilómetros entre el rendimiento de tu vehículo en km por litro. Eso te da los litros necesarios. Multiplícalos por el precio actual del litro para obtener el costo.",
      },
      {
        pregunta: "¿Cuánto rinde un carro en carretera?",
        respuesta:
          "Como referencia: un compacto ronda los 14 km/L, una SUV unos 10 y una pickup cerca de 8. El rendimiento real baja con velocidad alta, aire acondicionado, carga y pendientes.",
      },
    ],
  },

  tool_porcentaje: {
    secciones: [
      {
        titulo: "Los tres cálculos de porcentaje que realmente se usan",
        parrafos: [
          "Casi todo problema con porcentajes cae en uno de tres casos. <strong>Cuánto es el X% de una cantidad</strong>: multiplicas la cantidad por el porcentaje y divides entre 100. <strong>Qué porcentaje representa un número de otro</strong>: divides la parte entre el total y multiplicas por 100. <strong>Cuánto varió algo</strong>: restas el valor inicial del final, divides entre el inicial y multiplicas por 100.",
          "El tercero es el que más confusión genera, porque el resultado depende de cuál valor tomes como base. Un precio que sube de 100 a 150 aumentó 50%, pero si baja de 150 a 100 no disminuyó 50%, sino 33.3%.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cómo saco el porcentaje de una cantidad?",
        respuesta:
          "Multiplica la cantidad por el porcentaje y divide entre 100. El 15% de 2,000 es 2,000 × 15 ÷ 100 = 300.",
      },
      {
        pregunta: "¿Por qué subir 50% y bajar 50% no me devuelve al inicio?",
        respuesta:
          "Porque la base cambia. Si 100 sube 50% llega a 150; si 150 baja 50% llega a 75, no a 100. El porcentaje siempre se calcula sobre el valor del momento, no sobre el original.",
      },
    ],
  },

  tool_medidas: {
    secciones: [
      {
        titulo: "Sistema métrico y sistema imperial",
        parrafos: [
          "México usa el sistema métrico decimal, pero convive a diario con unidades imperiales: pulgadas en tornillos y pantallas, pies en alturas, libras en gimnasios y galones en recetas estadounidenses.",
          "Las equivalencias exactas importan más de lo que parece en construcción, cocina y compras internacionales. Una pulgada son exactamente 2.54 centímetros; una libra, 453.59237 gramos; un galón estadounidense, 3.78541 litros.",
          "La temperatura es el único caso que no se resuelve con un factor de multiplicación, porque las escalas tienen orígenes distintos: hay que sumar o restar antes de escalar.",
        ],
        lista: [
          "Celsius a Fahrenheit: (°C × 9/5) + 32",
          "Fahrenheit a Celsius: (°F − 32) × 5/9",
          "Celsius a Kelvin: °C + 273.15",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cuántos centímetros tiene una pulgada?",
        respuesta: "Exactamente 2.54 centímetros. No es una aproximación: es la definición oficial de la pulgada.",
      },
      {
        pregunta: "¿Cómo convierto Celsius a Fahrenheit?",
        respuesta:
          "Multiplica los grados Celsius por 9/5 y suma 32. Por ejemplo, 25 °C × 1.8 + 32 = 77 °F.",
      },
      {
        pregunta: "¿Cuántos kilos pesa una libra?",
        respuesta: "Una libra equivale a 0.45359237 kilogramos, es decir, unos 453.6 gramos.",
      },
    ],
  },

  tool_obra: {
    vigencia: false,
    secciones: [
      {
        titulo: "Cómo se arma el costo de una obra",
        parrafos: [
          "El presupuesto se construye partida por partida: cada concepto (cimentación, muros de block, losa, instalaciones, acabados) lleva una <strong>cantidad</strong> y un <strong>precio unitario</strong>. La cantidad sale de multiplicar tus metros cuadrados de construcción por cuántas unidades de ese concepto se necesitan por m².",
          "Sobre ese costo directo se agrega un porcentaje de <strong>desperdicio</strong> (mermas de material), los <strong>indirectos</strong> (administración, herramienta, supervisión), la <strong>utilidad</strong> del constructor y, al final, el <strong>IVA</strong>.",
        ],
      },
      {
        titulo: "Los precios son referencias, no cotizaciones",
        parrafos: [
          "Los precios unitarios precargados son referenciales para una casa de nivel medio en el norte de México y cambian por ciudad, proveedor y temporada. Edítalos con tus cotizaciones antes de tomar decisiones. Los rendimientos por m² también varían con el proyecto: una casa de dos plantas usa menos cimentación y losa por m² construido que una de una sola.",
          "Esta herramienta sirve para estimar y comparar escenarios; no sustituye al presupuesto de un arquitecto o ingeniero civil ni a un análisis de precios unitarios formal.",
        ],
      },
    ],
    faqs: [
      {
        pregunta: "¿Cuánto cuesta construir un metro cuadrado?",
        respuesta: "Depende del acabado, la ciudad y el proyecto. La calculadora parte de precios de referencia editables y te da el costo por m² resultante, con IVA, para que lo compares con tus cotizaciones.",
      },
      {
        pregunta: "¿Qué incluye el precio del muro de block pegado?",
        respuesta: "En la referencia incluye el block, el mortero y la mano de obra por m² de muro. Si tu albañil cobra la mano de obra aparte, ajusta el precio unitario o agrega una partida.",
      },
      {
        pregunta: "¿Por qué sumar desperdicio, indirectos y utilidad?",
        respuesta: "Los materiales se pierden en la obra, administrar y supervisar cuesta, y un contratista cobra utilidad. Omitirlos es la causa más común de presupuestos que se quedan cortos.",
      },
    ],
  },

  tool_sugerir: { vigencia: false },
};
